<template>
  <vx-card>
    <vue-element-loading
      :active="loading"
      color="rgba(var(--vs-primary), 1)"
      spinner="mini-spinner"/>

    <div v-if="reviewsSettingsAreValid">
      <div class="flex items-center">
        <h4 class="mr-3">
          {{ googleReviewsTitle }}
        </h4>
        <feather-icon
          icon='EditIcon'
          class="cursor-pointer"
          svgClasses='h-6 w-6'
          @click="activeModalReviewSettings=true"/>
      </div>
      <div class="flex mt-base justify-between">
        <div
          class="cursor-pointer hover:opacity-75 transition-opacity"
          @click="viewAllGoogleReviews">
          <p class="text-center text-4xl font-bold">{{ googleTotalReviews }}</p>
          <p class="text-center">
            {{ $tc('$General.Review', 2) | sentencecase }}
          </p>
        </div>
        <div
          class="cursor-pointer hover:opacity-75 transition-opacity"
          @click="viewAllGoogleReviews">
          <p class="text-center text-4xl font-bold">{{ googleRating | fixed_number(1) }}</p>
          <div class="flex justify-center">
            <star-rating
              :rating="googleRating"
              :increment="0.1"
              :round-start-rating="false"
              :show-rating="false"
              read-only
              :star-size="20"/>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
        <div class="text-center greet-user">
          <img
            src="@/assets/img/reviews/google-my-business-logo.svg"
            alt="Google my business"
            class="mx-auto"
            width="auto"
            height="120">

          <h1 class="mt-5">
            {{ $t('SetupGoogleReviews') }}
          </h1>

          <p class="xl:w-3/4 lg:w-4/5 md:w-2/3 w-4/5 mx-auto my-5">
            {{ $t('SetupMissingMsg') }}
          </p>

          <a
            href="#"
            class="link-popup"
            @click.prevent="activeModalReviewSettings=true">
            {{ $t('SetupNowButtonText') }}
          </a>
        </div>
    </div>

    <vs-divider class="my-base"></vs-divider>

    <div>
       <div class="flex items-center">
        <h4 class="mr-3">
          {{ internalReviewsTitle }}
        </h4>
        <feather-icon
          icon='EditIcon'
          class="cursor-pointer"
          svgClasses='h-6 w-6'
          @click="editAccountDetails"/>
      </div>
       <div class="flex mt-base justify-between">
        <div
          class="cursor-pointer hover:opacity-75 transition-opacity"
          @click="viewAllInternalReviews">
          <p class="text-center text-4xl font-bold">{{ internalTotalReviews }}</p>
          <p class="text-center">
            {{ $tc('$General.Review', 2) | sentencecase }}
          </p>
        </div>
        <div
          class="cursor-pointer hover:opacity-75 transition-opacity"
          @click="viewAllInternalReviews">
          <p class="text-center text-4xl font-bold">{{ internalRating | fixed_number(1) }}</p>
          <div class="flex justify-center">
            <star-rating
              :rating="internalRating"
              :increment="0.1"
              :round-start-rating="false"
              :show-rating="false"
              read-only
              :star-size="20"/>
          </div>
        </div>
      </div>
    </div>

    <vs-divider class="my-base"></vs-divider>



    <div class="flex justify-between items-center mt-base">
        <a
          href="#"
          class="flex items-center link-plain"
          @click.prevent="requestReviews()">
          <feather-icon
            icon='SendIcon'
            class="mr-1"
            svgClasses='h-5 w-5'/>
          <span class="link-text">
            {{ $t('RequestReviewButtonText') }}
          </span>
        </a>

        <!-- Case 1: Google reviews configured BUT no company details - Copy Google link + warning -->
        <a
          v-if="reviewsSettingsAreValid && !completedAccountDetails"
          href="#"
          class="ml-5 flex items-center link-plain"
          v-clipboard:copy="googlePlaceUrl"
          v-clipboard:success="onCopyGoogleLinkWithCompanyDetailsWarning"
          v-clipboard:error="onCopyLinkError"
          @click.prevent>
                <span class="link-text">
                  {{ $t('CopyReviewLinkLinkText') }}
                </span>
          <feather-icon
            icon='CopyIcon'
            class="ml-1"
            svgClasses='h-5 w-5'/>
        </a>

        <!-- Case 2: Company details complete BUT no Google reviews - Copy GrapePerks link + Google reviews suggestion -->
        <a
          v-else-if="!reviewsSettingsAreValid && completedAccountDetails"
          href="#"
          class="ml-5 flex items-center link-plain"
          v-clipboard:copy="reviewLink"
          v-clipboard:success="onCopyGrapePerksLinkWithGoogleSuggestion"
          v-clipboard:error="onCopyLinkError"
          @click.prevent>
                <span class="link-text">
                  {{ $t('CopyReviewLinkLinkText') }}
                </span>
          <feather-icon
            icon='CopyIcon'
            class="ml-1"
            svgClasses='h-5 w-5'/>
        </a>

        <!-- Case 3: Google reviews AND company details complete - Copy GrapePerks link + success -->
        <a
          v-else-if="reviewsSettingsAreValid && completedAccountDetails"
          href="#"
          class="ml-5 flex items-center link-plain"
          v-clipboard:copy="reviewLink"
          v-clipboard:success="onCopyGrapePerksLinkSuccess"
          v-clipboard:error="onCopyLinkError"
          @click.prevent>
                <span class="link-text">
                  {{ $t('CopyReviewLinkLinkText') }}
                </span>
          <feather-icon
            icon='CopyIcon'
            class="ml-1"
            svgClasses='h-5 w-5'/>
        </a>

        <!-- Case 4: Nothing configured - Show disabled button -->
        <a
          v-else
          href="#"
          class="ml-5 flex items-center link-plain opacity-50 cursor-pointer hover:opacity-75"
          @click.prevent="showSetupRequiredMessage">
                <span class="link-text">
                  {{ $t('CopyReviewLinkLinkText') }}
                </span>
          <feather-icon
            icon='CopyIcon'
            class="ml-1"
            svgClasses='h-5 w-5'/>
        </a>
      </div>

    <GmapMap
      v-show="false"
      ref="mapRef"
      :center="{ lat: 30,lng: 0 }">
    </GmapMap>

    <vs-popup
      :title="$t('SetupGoogleReviews')"
      fullscreen
      :active.sync="activeModalReviewSettings"
      class="mt-0 mb-0 pt-0 pb-0">
      <transition name="zoom-fade">
        <reviews-settings-create-or-edit
          v-if="activeModalReviewSettings"
          :card-style="false"
          @cancel="activeModalReviewSettings=false"
          @settings-updated="reviewsSettingsUpdated()"
          @request-reviews="requestReviewsFromReviewSettings()"/>
      </transition>
    </vs-popup>
  </vx-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { gmapApi } from 'gmap-vue';
import StarRating from 'vue-star-rating';
import reviewService from '@/api/modules/review.service';
import ReviewsSettingsCreateOrEdit from '@/views/modules/reviews/ReviewsSettingsCreateOrEdit.vue';
import VxTooltip from '@/layouts/components/vx-tooltip/VxTooltip.vue';

export default {
  name: 'ReviewsSummaryUnified',
  i18n: {
    messages: {
      en: {
        GoogleTitle: 'Google reviews ({businessName})',
        InternalTitle: 'Direct reviews ({businessName})',
        SetupGoogleReviews: 'Set up Google Reviews',
        SetupMissingMsg: 'Online reviews are the face of your business, reputation, and digital presence.',
        SetupNowButtonText: 'Set up now',
        CopyReviewLinkLinkText: 'Copy review link',
        RequestReviewButtonText: 'Request reviews',
      },
    },
  },
  components: {
    ReviewsSettingsCreateOrEdit,
    StarRating,
    VxTooltip,
  },
  data() {
    return {
      // Google Reviews
      googleBusinessName: '',
      googleRating: 0,
      googleTotalReviews: 0,
      googlePlaceUrl: '',
      googlePlaceDetailsFetched: false,
      activeModalReviewSettings: false,

      // Internal Reviews
      internalRating: 0,
      internalTotalReviews: 0,
      internalOverviewDataFetched: false,

      loading: true,
    };
  },
  computed: {
    ...mapGetters({
      getTenantFlag: 'auth/getTenantFlag',
      reviewsSettings: 'auth/tenantAccountSettingsReviews',
      tenantAccountBusinessName: 'auth/tenantAccountBusinessName',
      tenantShortReviewLink: 'auth/tenantShortReviewLink',
      tenantUrlLinksSuffix: 'auth/tenantUrlLinksSuffix',
      tenantId: 'auth/tenantId',
    }),
    googleReviewsTitle() {
      return this.$t('GoogleTitle', { businessName: this.googleBusinessName });
    },
    internalReviewsTitle() {
      return this.$t('InternalTitle', { businessName: this.tenantAccountBusinessName });
    },
    reviewsSettingsAreValid() {
      return this.getTenantFlag('completedReviewsSettings') && !!this.reviewsSettings.placeId;
    },
    googleMapsApi: gmapApi,
    reviewLink() {
      // Always return the internal GrapePerks review link, never the Google review link
      if (this.tenantUrlLinksSuffix) {
        const cleanPath = this.$enums.AppLinks.DASHBOARD.replace(/\/$/, '');
        return `${cleanPath}/review/${this.tenantUrlLinksSuffix}`;
      }
      // Fallback to the stored shortReviewLink if available
      return this.tenantShortReviewLink || '';
    },
    completedAccountDetails() {
      const result = this.getTenantFlag('completedAccountDetails');
      console.log('completedAccountDetails:', result);
      return result;
    },

  },
  async created() {
    this.loading = true;
    await this.fetchTenantOfUser();
    const promises = [];
    if (this.reviewsSettingsAreValid) {
      promises.push(this.getPlaceDetails(this.reviewsSettings.placeId));
    }
    if (this.completedAccountDetails) {
      promises.push(this.getInternalReviewsOverview());
    }
    await Promise.all(promises);
    this.loading = false;
  },
  methods: {
    ...mapActions({
      fetchTenantOfUser: 'auth/fetchTenantOfUser',
    }),
    async reviewsSettingsUpdated() {
      this.loading = true;
      await this.getPlaceDetails(this.reviewsSettings.placeId);
      this.loading = false;
    },
    async getPlaceDetails(placeId) {
      return new Promise(async (resolve, reject) => {
        if (!placeId) {
          return reject(new Error('There is no valid place id'));
        }

        const map = await this.$refs.mapRef.$mapPromise;
        const placesService = new this.googleMapsApi.maps.places.PlacesService(map);
        const request = {
          placeId,
        };

        placesService.getDetails(request, (place, status) => {
          if (
            status === this.googleMapsApi.maps.places.PlacesServiceStatus.OK
            && place
            && place.user_ratings_total
            && place.rating
          ) {
            this.googleBusinessName = place.name;
            this.googleTotalReviews = place.user_ratings_total;
            this.googleRating = place.rating;
            this.googlePlaceUrl = place.url;
            this.googlePlaceDetailsFetched = true;
            resolve();
          } else {
            reject(new Error('Error to get place details'));
          }
        });
      });
    },
    async getInternalReviewsOverview() {
      const { data } = await reviewService.getReviewsOverview();
      this.internalTotalReviews = data.count;
      this.internalRating = data.rating;
      this.internalOverviewDataFetched = true;
    },
    requestReviewsFromReviewSettings() {
      this.activeModalReviewSettings = false;
      setTimeout(() => {
        this.requestReviews();
      }, 0);
    },
    requestReviews() {
      this.$router.push(`/sms/campaigns/create?messageType=${this.$enums.Campaign.MessageType.TRANSACTIONAL}`);
    },
    viewAllGoogleReviews() {
      window.open(this.googlePlaceUrl, '_blank');
    },
    viewAllInternalReviews() {
      this.$router.push('/reviews');
    },
    editAccountDetails() {
      this.$router.push('/account/details');
    },
    // Case 1: Google reviews configured BUT no company details
    onCopyGoogleLinkWithCompanyDetailsWarning() {
      // Show success message for copying Google link
      this.$showSuccessActionNotification({
        title: 'Google Review link copied',
        text: 'The Google Review link has been copied to your clipboard.',
      });

      // Show warning with link to complete company details
      setTimeout(() => {
        this.$showGeneralWarningNotification({
          title: 'Complete your company details',
          text: 'To unlock your personalised GrapePerks review link, please complete your company details.',
        });
        // Navigate to account details after showing the warning
        setTimeout(() => {
          this.$router.push('/account/details');
        }, 3000);
      }, 1500);
    },

    // Case 2: Company details complete BUT no Google reviews
    onCopyGrapePerksLinkWithGoogleSuggestion() {
      // Show success message for copying GrapePerks link
      this.$showSuccessActionNotification({
        title: 'Direct review link copied',
        text: 'Your direct review link has been copied to your clipboard.',
      });

      // Show suggestion to set up Google reviews
      setTimeout(() => {
        this.$showGeneralWarningNotification({
          title: 'Boost your online visibility',
          text: 'Set up Google Reviews to reach more customers.',
        });
        // Navigate to Google reviews setup after showing the suggestion
        setTimeout(() => {
          this.activeModalReviewSettings = true;
        }, 3000);
      }, 1500);
    },

    // Case 3: Google reviews AND company details complete
    onCopyGrapePerksLinkSuccess() {
      this.$showSuccessActionNotification({
        title: 'Direct review link copied',
        text: 'Your direct review link has been copied to your clipboard. You\'re all set!',
      });
    },

    // Case 4: Nothing configured
    showSetupRequiredMessage() {
      this.$showGeneralWarningNotification({
        title: 'Setup required',
        text: 'Please complete your company details and Google Reviews setup to enable review link functionality.',
      });
      // Navigate to account details
      setTimeout(() => {
        this.$router.push('/account/details');
      }, 2000);
    },
    onCopyLinkError() {
      // This only runs when there is a link but copying failed
      this.$showGeneralErrorNotification({
        title: this.$t('$ReviewsSettingsModule.$SetupCompletedTab.CopyReviewLinkCopiedNotifyTitle'),
        text: this.$t('$ReviewsSettingsModule.$SetupCompletedTab.CopyReviewLinkCopiedNotifyErrorMsg'),
      });
    },
  },
};
</script>
